/**
 * useGateway — lazy single-gateway SDK loader singleton (Phase 5.3, D-07 / BILL-10).
 *
 * The contract behind `defineAsyncComponent` gateway forms: load ONLY the
 * tenant's configured gateway CDN script, lazily, exactly once per gateway, and
 * resolve when the corresponding `window` global becomes available. Selection is
 * data-driven — the caller passes the gateway resolved from the `/payment_methods`
 * fetch; there is deliberately NO `gatewayType` switch inside the loader beyond
 * the CDN-host lookup table.
 *
 * Design constraints (all CITED in 05.3-RESEARCH):
 *  - Types-only GPI import (`import type` only) so zero gateway runtime lands in
 *    the entry chunk; the CI bundle gate (Plan 07) asserts this (T-05.3-08).
 *  - Singleton cache keyed by gateway name → in-flight/resolved promise; the
 *    `<script>` is injected only once per gateway.
 *  - Delete-cache-entry-on-error: a failed CDN load evicts its entry so a retry
 *    can re-attempt (Pitfall 4 / T-05.3-09 — a sticky rejected promise must not
 *    brick the gateway forever).
 *  - NEVER auto-unload the injected global on teardown (VueUse `useScriptTag`
 *    defaults would rip the SDK out on unmount — Appendix B#10). `teardown()` is
 *    a no-op for the global; it only clears the in-flight cache.
 *  - Cross-gateway isolation (T-05.3-11): loading `globalpayments` never defines
 *    `window.StaxJs` and vice-versa.
 */

// Types-only — erases to zero runtime so no GPI code enters the entry chunk
// (BILL-10). This also augments `Window.GlobalPayments` with the GPI SDK type,
// so we only declare the Stax globals (Stax ships no npm package) here.
import type {} from '@globalpayments/js';

declare global {
  interface Window {
    StaxJs?: unknown;
    FattJs?: unknown;
  }
}

/** Data-driven gateway selector — the value resolved from `/payment_methods`. */
export type GatewayName = 'globalpayments' | 'stax';

interface GatewayDescriptor {
  /** CDN script URL injected lazily (CSP-allowlisted in Plan 05/07). */
  src: string;
  /** The `window` global whose presence marks the SDK ready. */
  global: keyof Window;
}

/**
 * CDN hosts (RESEARCH §"Gateway SDK load sources"). This is the ONLY place the
 * gateway name maps to anything — no behavioural switch lives in the loader.
 */
const GATEWAYS: Readonly<Record<GatewayName, GatewayDescriptor>> = {
  globalpayments: {
    src: 'https://js.paygateway.com/secure_payment/v1/globalpayments.js',
    global: 'GlobalPayments',
  },
  stax: {
    src: 'https://staxjs.staxpayments.com/staxjs-captcha.js',
    global: 'StaxJs',
  },
};

/**
 * Post-onload window for the gateway's `window` global to attach on a COLD load.
 *
 * The clock starts AFTER `script.onload` reports the CDN bundle loaded (see
 * `injectScript` — the production path resolves/rejects only from onload/onerror).
 * A genuine cold open must download + parse + evaluate globalpayments.js and
 * bootstrap up to 4 hosted-field iframes from the test CDN, so the window is set
 * generously (10s) to tolerate slow first loads. Raising this from the original
 * 2s closes the Test 1 cold-load gap (.planning/debug/gpi-card-form-cold-load-failure.md):
 * a too-tight timer rejected before the global attached, evicting the cache and
 * surfacing "We could not load the secure card form." If the global is genuinely
 * never going to appear (dead CDN) the form still surfaces a load error and the
 * cache entry is evicted for retry (Pitfall 4) — just after a longer grace.
 */
const GLOBAL_READY_TIMEOUT_MS = 10_000;
const GLOBAL_POLL_INTERVAL_MS = 25;

/**
 * Overall ceiling on the CDN script FETCH itself (TOF-601 "loads forever").
 *
 * A stalled CDN connection can fire NEITHER `script.onload` NOR `script.onerror`
 * — the socket just hangs. The post-onload `GLOBAL_READY_TIMEOUT_MS` clock cannot
 * save us here because it only starts INSIDE onload. Without a fetch-level ceiling
 * the injected-script promise never settles: `loadGateway` hangs forever, the
 * gateway form's `init()` never reaches `emit('mounted')`, and PayModal's form-load
 * spinner covers the card form indefinitely with NO error telemetry (onerror →
 * `captureGatewaySdkLoadFailure` never fires). This is the dominant TOF-601 report.
 *
 * Armed at script-append time, cleared by onload/onerror. On expiry it rejects, so
 * `loadGateway` evicts the cache entry (Pitfall 4) for retry and the form surfaces
 * its inline error instead of an infinite spinner. Set generously (20s) so a slow-
 * but-working cold load — DNS + TCP + TLS + download + parse of the Stax/Spreedly
 * bundle before onload even fires — is never pre-empted (cf. the Test 1 cold-load
 * gap that a too-tight 2s timer caused). Once onload fires, the separate 10s
 * `GLOBAL_READY_TIMEOUT_MS` takes over the global-attach window.
 */
const SCRIPT_LOAD_TIMEOUT_MS = 20_000;

/**
 * Module-level singleton cache. Keyed by gateway name → the in-flight (or
 * resolved) load promise. Surviving module scope is what makes the loader a
 * process-wide singleton: a second `loadGateway(name)` returns the same promise.
 */
const cache = new Map<GatewayName, Promise<unknown>>();

function waitForGlobal(globalKey: keyof Window): Promise<unknown> {
  return new Promise<unknown>((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      if (typeof window !== 'undefined' && window[globalKey] !== undefined) {
        resolve(window[globalKey]);
        return;
      }
      if (Date.now() - start >= GLOBAL_READY_TIMEOUT_MS) {
        reject(
          new Error(
            `[useGateway] timed out waiting for window.${String(globalKey)}`,
          ),
        );
        return;
      }
      setTimeout(check, GLOBAL_POLL_INTERVAL_MS);
    };
    check();
  });
}

function injectScript(descriptor: GatewayDescriptor): Promise<unknown> {
  return new Promise<unknown>((resolve, reject) => {
    // If the global is already present, resolve immediately (idempotent).
    if (
      typeof window !== 'undefined' &&
      window[descriptor.global] !== undefined
    ) {
      resolve(window[descriptor.global]);
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${descriptor.src}"]`,
    );
    if (existing) {
      // Script already in the DOM — only the global needs to appear.
      waitForGlobal(descriptor.global).then(resolve, reject);
      return;
    }

    const script = document.createElement('script');
    script.src = descriptor.src;
    script.async = true;

    // Fetch-level ceiling: a stalled CDN connection fires neither onload nor
    // onerror. Without this the promise never settles and the caller hangs
    // forever (TOF-601). Cleared the instant onload/onerror fires so it can
    // never pre-empt a legitimate (even slow) load.
    const loadTimer = setTimeout(() => {
      reject(new Error(`[useGateway] timed out loading ${descriptor.src}`));
    }, SCRIPT_LOAD_TIMEOUT_MS);

    script.onload = () => {
      clearTimeout(loadTimer);
      // The global may attach asynchronously after onload — poll for it.
      waitForGlobal(descriptor.global).then(resolve, reject);
    };
    script.onerror = () => {
      clearTimeout(loadTimer);
      reject(new Error(`[useGateway] failed to load ${descriptor.src}`));
    };
    document.head.appendChild(script);

    // TEST-ONLY fallback. jsdom/happy-dom never fire load/error on injected
    // <script>s, so under Vitest we poll the global directly: a test that stubs
    // the global resolves, and one that never does rejects on timeout (entry
    // then evicted for retry). This MUST stay gated to the test env — in a real
    // browser it would start a timeout clock at APPEND time (before the cold
    // network fetch completes) and pre-empt the onload-anchored wait, rejecting
    // a perfectly good cold load (the Test 1 gap; see the debug doc). In
    // production the only resolution paths are script.onload (resolve, via the
    // onload-anchored waitForGlobal above) and script.onerror (reject).
    if (import.meta.env.MODE === 'test') {
      waitForGlobal(descriptor.global).then(
        (value) => {
          clearTimeout(loadTimer);
          resolve(value);
        },
        (err) => {
          clearTimeout(loadTimer);
          reject(err);
        },
      );
    }
  });
}

export interface UseGateway {
  /**
   * Lazily load the requested gateway's CDN SDK exactly once. Resolves with the
   * gateway's `window` global once it is available; on failure the cache entry
   * is deleted so a subsequent call retries (Pitfall 4).
   */
  loadGateway: (name: GatewayName) => Promise<unknown>;
  /**
   * No-op for the injected global (we never rip the SDK out on unmount). Only
   * clears any in-flight cache entries so a fresh modal session can re-load.
   * Resolved entries are left intact — the global survives.
   */
  teardown: () => void;
}

export function useGateway(): UseGateway {
  function loadGateway(name: GatewayName): Promise<unknown> {
    const cached = cache.get(name);
    if (cached) return cached;

    const descriptor = GATEWAYS[name];
    const promise = injectScript(descriptor).catch((err) => {
      // Pitfall 4: evict the failed entry so a retry can re-attempt.
      cache.delete(name);
      throw err;
    });
    cache.set(name, promise);
    return promise;
  }

  function teardown(): void {
    // Intentionally does NOT remove injected <script>s or window globals
    // (Appendix B#10 — no auto-unload). The SDK stays resident for the session.
  }

  return { loadGateway, teardown };
}
